import {
  JepxSpotPortion,
  PlanSourceType,
} from '@ebalance/proto/bfftypes/v1/plan_submission_pb'
import type { GenerationSalesPlanGenerationRow } from '@ebalance/proto/bffuser/v1/generation_sales_plan_model_pb'
import type { PlanSlot } from '@ebalance/proto/bffuser/v1/procurement_plan_model_pb'

import { PLAN_SOURCE_TYPE_I18N_KEYS } from '~/pages/GenerationSalesPlanDetail/constants'
import {
  type SlotValue,
  calcColumnDiff,
  makeColumnKey,
  rowToSlots,
} from '~/pages/GenerationSalesPlanDetail/models/calc'

/** 発電販売 / 調達の側 (= 内訳の方向) */
export type ColumnSide = 'procurement' | 'sales'

/** 列キー解決に必要な最小フィールド（proto 行と構造的に互換。optional フィールド） */
export type ColumnRowLike = {
  sourceType: PlanSourceType
  counterpartyCode?: string
  /**
   * JEPX スポット調達の 相対契約分 / 調達先未定分 を振り分ける型付き信号。worker が付与し
   * backend converter が proto enum に載せる（counterparty_name 文字列照合に依存しない）。
   */
  jepxSpotPortion?: JepxSpotPortion
  contractId?: string
}

/** 列を組み立てる入力行（内訳行。proto GenerationSalesPlanBreakdownRow と構造的に互換） */
export type BreakdownSourceRow = ColumnRowLike & {
  counterpartyName?: string
  slots: PlanSlot[]
}

/**
 * テーブルに表示する内訳列 1 列分の表示情報（index.tsx が組み立て、PlanTable が描画する
 * 表示行の契約）。JEPX 市場列と取引先列の両方をこの型で表す。
 * ProcurementPlanDetail の BreakdownColumn と等価な構造（page-local に複製）。
 */
export type BreakdownColumn = {
  /** 列のキー（一意識別子） */
  key: string
  /** 列見出しラベル（取引先名 or 市場名。取引先コードは counterpartyCode で別途保持） */
  label: string
  /** 取引先コード（取引先列の 1 段目）。市場列では undefined */
  counterpartyCode: string | undefined
  /** この列の sourceType（PlanSourceType の値） */
  sourceType: PlanSourceType
  /** 48 スロットの表示値 */
  slots: SlotValue[]
  /** コマ単位の前回差分フラグ */
  diffFlags: boolean[]
  /**
   * JEPX 市場列のヘッダラベル。設定時は取引先 2 段ヘッダではなく
   * code（JSPT3 / J1HR3）+ primary（前日スポット / 時間前）+ secondary（(相対契約分) 等）
   * の固定ラベルを表示する。
   */
  fixedLabel?: { code: string; primary: string; secondary: string | undefined }
}

/** 発電BG別内訳 1 列分の表示情報（発電計画合計の内訳。本画面では列として表示しないが、
 * 将来のドリルダウン UI 再利用のため purely functional に組み立てておく）。 */
export type GenerationColumn = {
  bgCode: string
  bgName: string | undefined
  slots: SlotValue[]
}

/**
 * 発電BG別内訳行から GenerationColumn[] を組み立てる純関数。
 * マスタ登録順（= 行順）をそのまま維持する。
 */
export function buildGenerationColumns(
  rows: GenerationSalesPlanGenerationRow[],
): GenerationColumn[] {
  return rows.map((row) => ({
    bgCode: row.generationBgCode,
    bgName: row.generationBgName,
    slots: rowToSlots(row.slots),
  }))
}

/**
 * JEPX 市場列 1 列分の仕様。
 * `key` は列の安定 ID であり、編集バッファ（slotsMap）のキーにもなる。
 */
export type JepxMarketSpec = {
  key: string
  /**
   * OCCTO の取引相手コード（ヘッダ最上段に表示）。JEPX スポット = JSPT3、
   * JEPX 時間前 = J1HR3。制度上固定の安定コードのため i18n しない。
   */
  code: string
  /** ヘッダ中段の i18n キー（market.jepxSpot=前日スポット / market.jepxIntraday=時間前） */
  primaryKey: string
  /** ヘッダ下段の i18n キー（(相対契約分) / (調達先未定分)）。無ければ省略 */
  secondaryKey?: string
  /**
   * この列を出すトリガーとなる値行の sourceType。マスタにこの市場の取引先が
   * 登録され、worker が当該 sourceType の値行を生成した時だけ列を出す（= 常時固定
   * 表示はしない）。
   */
  sourceType: PlanSourceType
}

/** JEPX スポット市場の OCCTO 取引相手コード */
const JEPX_SPOT_CODE = 'JSPT3'
/** JEPX 時間前市場の OCCTO 取引相手コード */
const JEPX_INTRADAY_CODE = 'J1HR3'

/**
 * JEPX 市場列の安定キー。resolveColumnKey（行 → 列キー）と JEPX_MARKET_SPECS（列ヘッダ）
 * が同じ語彙を共有することで、二重定義のドリフト（= JEPX 行が取引先列に漏れる silent bug）
 * を防ぐ。ProcurementPlanDetail/models/columns.ts と同一命名。
 */
const JEPX_COLUMN_KEY = {
  spotBilateral: 'jepx-spot-bilateral',
  spotUndetermined: 'jepx-spot-undetermined',
  spotSales: 'jepx-spot',
  intraday: 'jepx-intraday',
} as const

const marketKey = (k: string): string =>
  `page:generationSalesPlanDetail.market.${k}`

/**
 * JEPX 市場列の仕様（先頭・canonical 順）。値行に対応 sourceType が存在する時だけ
 * `buildBreakdownColumns` が出力する（マスタ取引先駆動。常時固定表示はしない。
 * ProcurementPlanDetail/models/columns.ts と同方針）。
 *
 * - 調達: 前日スポット(相対契約分) → 前日スポット(調達先未定分) → 時間前
 * - 販売: 前日スポット → 時間前（スポットは分割しない）
 */
export const JEPX_MARKET_SPECS: Record<ColumnSide, JepxMarketSpec[]> = {
  procurement: [
    {
      key: JEPX_COLUMN_KEY.spotBilateral,
      code: JEPX_SPOT_CODE,
      primaryKey: marketKey('jepxSpot'),
      secondaryKey: marketKey('bilateralPortion'),
      sourceType: PlanSourceType.JEPX_SPOT,
    },
    {
      key: JEPX_COLUMN_KEY.spotUndetermined,
      code: JEPX_SPOT_CODE,
      primaryKey: marketKey('jepxSpot'),
      secondaryKey: marketKey('undeterminedPortion'),
      sourceType: PlanSourceType.JEPX_SPOT,
    },
    {
      key: JEPX_COLUMN_KEY.intraday,
      code: JEPX_INTRADAY_CODE,
      primaryKey: marketKey('jepxIntraday'),
      sourceType: PlanSourceType.JEPX_INTRADAY,
    },
  ],
  sales: [
    {
      key: JEPX_COLUMN_KEY.spotSales,
      code: JEPX_SPOT_CODE,
      primaryKey: marketKey('jepxSpot'),
      sourceType: PlanSourceType.JEPX_SPOT,
    },
    {
      key: JEPX_COLUMN_KEY.intraday,
      code: JEPX_INTRADAY_CODE,
      primaryKey: marketKey('jepxIntraday'),
      sourceType: PlanSourceType.JEPX_INTRADAY,
    },
  ],
}

const JEPX_KEY_SET: Record<ColumnSide, ReadonlySet<string>> = {
  procurement: new Set(JEPX_MARKET_SPECS.procurement.map((s) => s.key)),
  sales: new Set(JEPX_MARKET_SPECS.sales.map((s) => s.key)),
}

/** 与えられた列キーが JEPX 市場列のものか */
export function isJepxMarketKey(key: string, side: ColumnSide): boolean {
  return JEPX_KEY_SET[side].has(key)
}

/**
 * 内訳行が属する列キーを解決する。
 *
 * - JEPX スポット: 調達は jepxSpotPortion(型付き enum) で 相対契約分 / 調達先未定分 に
 *   振り分ける（worker は 2 行とも counterparty_code=JSPT3 で出し、portion で区別する）。
 *   販売は分割せず 1 列。
 * - JEPX 時間前: 1 列。
 * - それ以外（BG 相対 / eClear / 常時BU 等）: 取引先別の makeColumnKey。
 */
export function resolveColumnKey(row: ColumnRowLike, side: ColumnSide): string {
  if (row.sourceType === PlanSourceType.JEPX_SPOT) {
    if (side === 'sales') return JEPX_COLUMN_KEY.spotSales
    // portion=BILATERAL → 相対契約分列 / それ以外（UNDETERMINED / UNSPECIFIED）→ 調達先未定分列
    return row.jepxSpotPortion === JepxSpotPortion.BILATERAL
      ? JEPX_COLUMN_KEY.spotBilateral
      : JEPX_COLUMN_KEY.spotUndetermined
  }
  if (row.sourceType === PlanSourceType.JEPX_INTRADAY) {
    return JEPX_COLUMN_KEY.intraday
  }
  return makeColumnKey(row.sourceType, row.counterpartyCode, row.contractId)
}

/**
 * 同一列キーに複数行が来た場合のスロット合算。
 * 両方未入力なら未入力、片方でも数値があれば数値同士を合算（未入力は 0 扱い）。
 */
export function mergeSlots(
  into: SlotValue[] | undefined,
  add: SlotValue[],
): SlotValue[] {
  if (into === undefined) return [...add]
  return into.map((v, i) => {
    const w = add[i]
    if (v === undefined && w === undefined) return undefined
    return (v ?? 0) + (w ?? 0)
  })
}

// 値行が 1 つも無い内訳列（例: JEPX 約定 source 未配線で行が来ない 相対契約分）は
// 業務的に「取引なし = 0」を表す。0 埋めで列を出し、"-"（cell.empty）にしない。
const emptySlots = (): SlotValue[] => Array<SlotValue>(48).fill(0)

/**
 * 表示する内訳列を組み立てる純関数。
 *
 * 列はすべて値行（= backend worker が生成した記録）から導出する。常時固定表示する列は無い:
 *
 * 1. JEPX 市場列（先頭・canonical 順）: 値行に対応 sourceType がある時だけ出す。
 *    調達 JEPXスポットは構造的に 相対契約分 / 調達先未定分 の 2 列に分割。
 * 2. 取引先別内訳列（非 JEPX 行のみ。同一列キーは 1 度だけ、マスタ codes[] 順 = 行順）。
 *
 * 純関数（slotsMap / prevMap と i18n 解決関数 `translate` を注入）なので単体テスト可能。
 * ProcurementPlanDetail/models/columns.ts buildBreakdownColumns と等価の構成。
 */
export function buildBreakdownColumns(params: {
  rows: BreakdownSourceRow[]
  side: ColumnSide
  /** i18n キー → 文言。呼び出し側の useTranslation().t を渡す */
  translate: (key: string) => string
  /** 前回提出データがあるか（差分フラグ算出に使う） */
  hasPrev?: boolean
  /** 前回提出時の列マップ（列キー → 48 スロット） */
  prevMap?: Map<string, SlotValue[]>
}): BreakdownColumn[] {
  const { rows, side, translate } = params
  const hasPrev = params.hasPrev ?? false
  const prevMap = params.prevMap ?? new Map<string, SlotValue[]>()

  // 列キーごとにスロットを合算する（同一列キーに複数行が来た場合の防御）
  const slotsMap = new Map<string, SlotValue[]>()
  for (const row of rows) {
    const key = resolveColumnKey(row, side)
    slotsMap.set(key, mergeSlots(slotsMap.get(key), rowToSlots(row.slots)))
  }

  const diffFor = (slots: SlotValue[], key: string): boolean[] =>
    hasPrev
      ? calcColumnDiff(slots, prevMap, key)
      : Array<boolean>(48).fill(false)

  const cols: BreakdownColumn[] = []

  // 1) JEPX 市場列: 値行に該当 sourceType がある時だけ（= マスタに JEPX 取引先がある時だけ）
  //    canonical 順で先頭に出す。常時固定表示はしない。
  const presentSourceTypes = new Set(rows.map((r) => r.sourceType))
  for (const spec of JEPX_MARKET_SPECS[side]) {
    if (!presentSourceTypes.has(spec.sourceType)) continue
    const slots = slotsMap.get(spec.key) ?? emptySlots()
    cols.push({
      key: spec.key,
      label: translate(spec.primaryKey),
      counterpartyCode: undefined,
      sourceType: spec.sourceType,
      fixedLabel: {
        code: spec.code,
        primary: translate(spec.primaryKey),
        secondary:
          spec.secondaryKey !== undefined
            ? translate(spec.secondaryKey)
            : undefined,
      },
      slots,
      diffFlags: diffFor(slots, spec.key),
    })
  }

  // 2) 取引先別内訳列（非 JEPX 行のみ。同一列キーは 1 度だけ）
  const seen = new Set<string>()
  for (const row of rows) {
    const key = resolveColumnKey(row, side)
    if (isJepxMarketKey(key, side)) continue
    if (seen.has(key)) continue
    seen.add(key)
    const slots = slotsMap.get(key) ?? emptySlots()
    const sourceLabelKey =
      PLAN_SOURCE_TYPE_I18N_KEYS[row.sourceType] ??
      'page:generationSalesPlanDetail.sourceType.unspecified'
    cols.push({
      key,
      label: row.counterpartyName ?? translate(sourceLabelKey),
      counterpartyCode: row.counterpartyCode,
      sourceType: row.sourceType,
      slots,
      diffFlags: diffFor(slots, key),
    })
  }
  return cols
}
